import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { coins as coinsApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Coins, Sparkles, Clock, TrendingUp, Gift } from 'lucide-react';

export default function MyCoins() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [revealing, setRevealing] = useState(null);

  const load = () => coinsApi.me().then(setData).catch(() => {});
  useEffect(() => { if (user) load(); }, [user?.id]);

  if (!user) return <div className="max-w-2xl mx-auto px-4 py-12 text-center text-ink/60">Please log in to see your MandiCoins.</div>;
  if (!data) return <div className="max-w-2xl mx-auto px-4 py-12 text-ink/60">Loading…</div>;

  const reveal = async (card) => {
    setRevealing(card.id);
    try {
      await coinsApi.scratch(card.order_id);
      await load();
    } catch (e) { console.warn('[coins] scratch failed', e); } finally { setRevealing(null); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-12">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFF4E6] to-[#FFD180] text-[#8E3B00] grid place-items-center"><Coins className="w-6 h-6"/></div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">MandiCoins</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl">Your rewards wallet</h1>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-6">
        <Stat icon={Coins} label="Spendable" value={data.balance} suffix="🪙" testId="coins-balance" highlight />
        <Stat icon={Clock} label="Locked (unlocks on delivery)" value={data.locked} suffix="🪙" testId="coins-locked"/>
        <Stat icon={TrendingUp} label="Lifetime earned" value={data.lifetime_earned} suffix="🪙" testId="coins-lifetime"/>
      </div>
      <div className="text-xs text-ink/60 mt-2">1 coin = ₹{data.coin_value_inr.toFixed(2)}. Coins expire 6 months from earning. Max 20% of subtotal redeemable per order.</div>

      <section className="card-soft p-5 mt-8">
        <div className="font-heading font-bold text-lg inline-flex items-center gap-2"><Gift className="w-5 h-5 text-[#E67E22]"/>Scratch cards</div>
        <p className="text-xs text-ink/60 mt-1">A scratch card lands every time an order is delivered. Tap to reveal!</p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {data.scratch_cards.length === 0 && (
            <div className="col-span-full text-sm text-ink/50 py-8 text-center">No cards yet. Complete an order to earn one.</div>
          )}
          {data.scratch_cards.map(c => (
            <div
              key={c.id}
              data-testid={`scratch-card-${c.id}`}
              onClick={() => !c.scratched && reveal(c)}
              className={`aspect-[5/3] rounded-xl p-3 flex flex-col justify-between cursor-${c.scratched ? 'default' : 'pointer'} border ${c.scratched ? 'bg-gradient-to-br from-[#FFF4E6] to-[#FFD180] border-[#F2A157]' : 'bg-gradient-to-br from-[#8E3B00] to-[#D35400] border-[#5E2700] text-white'}`}
            >
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">
                {c.scratched ? c.tier_label || 'Reward' : 'Tap to scratch'}
              </div>
              <div className="text-right">
                {c.scratched ? (
                  <div className="font-heading font-black text-2xl text-[#8E3B00]">{c.coins}🪙</div>
                ) : (
                  <Sparkles className="w-6 h-6 ml-auto opacity-80"/>
                )}
              </div>
              {revealing === c.id && <div className="text-[10px] opacity-80">Scratching…</div>}
            </div>
          ))}
        </div>
      </section>

      <section className="card-soft p-5 mt-6">
        <div className="font-heading font-bold text-lg">Coin ledger</div>
        <div className="mt-3 divide-y divide-border text-sm">
          {data.ledger.length === 0 && <div className="text-ink/50 py-6 text-center">No activity yet.</div>}
          {data.ledger.map(l => (
            <div key={l.id} className="py-2.5 flex items-center justify-between">
              <div>
                <div className="font-semibold capitalize">{l.type === 'earn' ? 'Earned' : 'Redeemed'} · {l.source.replace('_', ' ')}</div>
                <div className="text-xs text-ink/60">{new Date(l.created_at).toLocaleString()}{l.order_id ? ` · order #${l.order_id.slice(0,8)}` : ''}</div>
              </div>
              <div className={`font-mono font-bold ${l.type === 'earn' ? 'text-[#1B5E20]' : 'text-[#9A4500]'}`}>
                {l.type === 'earn' ? '+' : '–'}{l.amount}🪙
                <div className="text-[10px] uppercase tracking-wider text-ink/50">{l.status}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-6 text-center">
        <Link to="/stores" className="btn-primary inline-flex">Spend coins on a new order</Link>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, suffix, testId, highlight }) {
  return (
    <div data-testid={testId} className={`card-soft p-4 ${highlight ? 'border-2 border-[#E67E22] bg-[#FFF4E6]' : ''}`}>
      <div className="flex items-center justify-between text-ink/60 text-[11px] uppercase tracking-[0.18em] font-bold">
        <span>{label}</span>
        <Icon className="w-4 h-4"/>
      </div>
      <div className="font-heading font-black text-3xl mt-1">{value}<span className="text-xl ml-1">{suffix}</span></div>
    </div>
  );
}
